import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Input } from '@/shared/ui/Input';
import { StatusBadge } from '@/shared/ui/StatusBadge';
import { SkeletonList } from '@/shared/ui/Skeleton';
import { ErrorState } from '@/shared/ui/ErrorState';
import { Dialog } from '@/shared/ui/Dialog';
import { Icon } from '@/shared/ui/Icon';
import { useToast } from '@/shared/ui/Toast';
import { createRoomMeeting, getRoom, listRoomMeetings, type RoomMeeting } from '@/shared/api/rooms';
import { MemoryCalendar } from '@/features/room/MemoryCalendar';
import { fmtKst } from '@/shared/lib/date';

const AVATAR_TONES = ['bg-primary', 'bg-success', 'bg-warning', 'bg-primary-strong'] as const;

function initialOf(name: string): string {
  return name.trim().charAt(0) || '멤';
}

function avatarTone(index: number): string {
  return AVATAR_TONES[index % AVATAR_TONES.length];
}

function meetingDateParts(meeting: RoomMeeting): { day: string; month: string; tone: string } {
  if (!meeting.confirmedDate) {
    return { day: '미정', month: '날짜', tone: 'bg-warning-soft text-warning-strong' };
  }
  const date = new Date(meeting.confirmedDate);
  return {
    day: String(date.getDate()),
    month: `${date.getMonth() + 1}월`,
    tone: 'bg-primary-soft text-primary',
  };
}

export default function RoomPage() {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState('');
  const [region, setRegion] = useState('');

  const roomQ = useQuery({ queryKey: ['room', id], queryFn: () => getRoom(id), enabled: !!id });
  const meetingsQ = useQuery({
    queryKey: ['room-meetings', id],
    queryFn: () => listRoomMeetings(id),
    enabled: !!id,
  });

  const createMut = useMutation({
    mutationFn: () =>
      createRoomMeeting(id, {
        title: title.trim(),
        ...(region.trim() ? { region: region.trim() } : {}),
      }),
    onSuccess: (meeting) => {
      qc.invalidateQueries({ queryKey: ['room-meetings', id] });
      qc.invalidateQueries({ queryKey: ['meetings'] });
      setCreating(false);
      setTitle('');
      setRegion('');
      toast.success('모임을 만들었어요. 멤버 전원이 자동 참여됐어요.');
      navigate(`/meetings/${meeting.id}`);
    },
    onError: () => toast.error('모임 생성에 실패했어요.'),
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!title.trim() || createMut.isPending) return;
    createMut.mutate();
  }

  if (roomQ.isLoading) return <SkeletonList count={3} />;
  if (roomQ.isError || !roomQ.data) {
    return <ErrorState title="모임방을 불러오지 못했어요" onRetry={() => roomQ.refetch()} />;
  }
  const room = roomQ.data;
  const meetingsRaw = meetingsQ.data;
  const meetings = Array.isArray(meetingsRaw) ? meetingsRaw : [];
  const members = Array.isArray(room.members) ? room.members : [];
  const memories = meetings
    .filter((m) => m.status === 'CLOSED' && m.confirmedDate)
    .map((m) => ({ id: m.id, title: m.title, date: new Date(m.confirmedDate!) }));
  const visibleMembers = members.slice(0, 4);
  const hiddenMemberCount = Math.max(members.length - visibleMembers.length, 0);

  return (
    <section className="space-y-4 pb-2" aria-label={`${room.name} 모임방`}>
      <header className="flex items-center gap-2 border-b border-surface-border bg-surface pb-3 pt-1">
        <Link
          to="/rooms"
          aria-label="모임방 목록으로 돌아가기"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-surface-muted text-ink-muted focus-visible:outline-none focus-visible:shadow-focus-ring"
        >
          <Icon name="back" size={16} />
        </Link>
        <h1 className="min-w-0 flex-1 truncate text-[17px] font-semibold tracking-[-0.4px] text-ink">
          {room.name}
        </h1>
        <button
          type="button"
          onClick={() => setCreating(true)}
          className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-primary-soft px-2.5 py-1.5 text-[11px] font-semibold text-primary transition-colors hover:bg-primary-light/60 focus-visible:outline-none focus-visible:shadow-focus-ring"
        >
          <Icon name="plus" size={13} />
          약속
        </button>
      </header>

      <section className="relative overflow-hidden rounded-[18px] bg-hero px-4 py-[17px] text-white shadow-[var(--hero-glow)] before:absolute before:-right-7 before:-top-7 before:h-[110px] before:w-[110px] before:rounded-full before:bg-white/10">
        <div className="relative text-[10px] font-semibold uppercase tracking-[0.04em] text-white/70">Room</div>
        <div className="relative mt-2 line-clamp-2 text-[20px] font-semibold leading-tight tracking-[-0.6px]">
          {room.name}
        </div>
        <p className="relative mt-2 text-[12px] text-white/75">
          {members.length}명 참여 · 약속 {meetings.length}개 진행
        </p>
        {room.description && <p className="relative mt-1.5 line-clamp-2 text-[12px] text-white/70">{room.description}</p>}
      </section>

      <section aria-labelledby="room-members-heading" className="space-y-2">
        <div className="flex items-center justify-between px-0.5">
          <h2 id="room-members-heading" className="text-[13px] font-semibold text-ink">참여 멤버</h2>
        </div>
        <div className="rounded-[22px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm px-3.5 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center">
              <div className="flex -space-x-1.5" aria-label="모임방 멤버">
                {visibleMembers.map((member, index) => (
                  <span
                    key={member.userId}
                    title={member.nickname}
                    className={`flex h-[26px] w-[26px] items-center justify-center rounded-full border-2 border-white text-[9px] font-semibold text-white ${avatarTone(index)}`}
                  >
                    {initialOf(member.nickname)}
                  </span>
                ))}
                {hiddenMemberCount > 0 && (
                  <span className="flex h-[26px] min-w-[34px] items-center justify-center rounded-full border-2 border-surface bg-surface-muted px-2 text-[9px] font-semibold text-ink-muted">
                    외 {hiddenMemberCount}
                  </span>
                )}
              </div>
              <span className="ml-2 truncate text-[10.5px] text-ink-faint">{members.length}명 참여 중</span>
            </div>
            <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-success-soft px-2 py-1 text-[10px] font-medium text-success-strong">
              <span className="h-1.5 w-1.5 rounded-full bg-current" />
              활성
            </span>
          </div>
        </div>
      </section>

      {memories.length > 0 && (
        <section className="rounded-[22px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm p-3.5" aria-labelledby="memory-calendar-heading">
          <h2 id="memory-calendar-heading" className="text-[13px] font-semibold text-ink">추억 캘린더</h2>
          <p className="mt-1 text-[11px] text-ink-muted">끝난 모임이 추억으로 남아요. 점이 찍힌 날짜를 눌러보세요.</p>
          <div className="mt-4">
            <MemoryCalendar memories={memories} />
          </div>
        </section>
      )}

      <section aria-labelledby="room-meetings-heading" className="overflow-hidden rounded-[22px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm">
        <div className="flex items-center justify-between border-b border-surface-border/60 px-3.5 py-3">
          <h2 id="room-meetings-heading" className="text-[13px] font-semibold text-ink">약속 목록</h2>
          <button
            type="button"
            onClick={() => setCreating(true)}
            className="inline-flex items-center gap-1 rounded-lg bg-primary-soft px-2.5 py-1 text-[11px] font-semibold text-primary focus-visible:outline-none focus-visible:shadow-focus-ring"
          >
            <Icon name="plus" size={12} />
            약속
          </button>
        </div>
        {meetings.length === 0 ? (
          <p className="px-3.5 py-8 text-center text-[13px] text-ink-muted">아직 모임이 없어요. 첫 모임을 만들어보세요.</p>
        ) : (
          <ul aria-label="모임 목록">
            {meetings.map((meeting) => {
              const dateParts = meetingDateParts(meeting);
              return (
                <li key={meeting.id} className="border-t border-surface-border first:border-t-0">
                  <Link
                    to={`/meetings/${meeting.id}`}
                    className="pressable flex items-center gap-2.5 px-3.5 py-3 transition-colors hover:bg-surface-muted focus-visible:outline-none focus-visible:shadow-focus-ring"
                  >
                    <span className={`flex h-[38px] w-[38px] shrink-0 flex-col items-center justify-center rounded-xl ${dateParts.tone}`}>
                      <span className="text-[13px] font-semibold leading-none">{dateParts.day}</span>
                      <span className="mt-0.5 text-[9px] font-semibold">{dateParts.month}</span>
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[14px] font-semibold tracking-[-0.2px] text-ink">{meeting.title}</span>
                      <span className="mt-1 block truncate text-[11px] text-ink-muted">
                        {meeting.region ? `${meeting.region} · ` : ''}
                        {meeting.confirmedDate ? fmtKst(meeting.confirmedDate) : '일정 미정'}
                      </span>
                    </span>
                    <StatusBadge status={meeting.status} />
                    <Icon name="right" size={15} className="shrink-0 text-ink-faint" />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <Dialog
        open={creating}
        title="이 방에서 새 모임"
        description="모임방 멤버 전원이 자동으로 참여해요."
        onCancel={() => {
          if (createMut.isPending) return;
          setCreating(false);
        }}
        confirmLabel={createMut.isPending ? '만드는 중…' : '만들기'}
        confirmDisabled={createMut.isPending || title.trim().length === 0}
        onConfirm={() => onSubmit({ preventDefault: () => {} } as FormEvent)}
      >
        <form onSubmit={onSubmit} className="space-y-3">
          <Input
            label="모임 제목"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="예) 이번 주 금요일 저녁"
            maxLength={50}
            required
            aria-required="true"
            autoFocus
          />
          <Input
            label="지역 (선택)"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            placeholder="강남, 성수 등"
            maxLength={30}
          />
        </form>
      </Dialog>
    </section>
  );
}
